/**
 * Admin — Oportunidades IA — Panel de propietario.
 * /admin/ops/ai/insights
 */
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Lightbulb, AlertTriangle, Users, TrendingUp, Loader2 } from "lucide-react";

export const Route = createFileRoute("/admin/ops/ai/insights")({
  component: AdminAIInsightsPage,
  head: () => ({ meta: [{ title: "Oportunidades IA — Admin — Structura" }] }),
});

interface GlobalSummary {
  total_tenants: number;
  total_open: number;
  total_critical: number;
  total_applied: number;
  avg_score: number;
}

interface TopInsight {
  insight_id: string;
  frequency: number;
}

function AdminAIInsightsPage() {
  const [summary, setSummary] = useState<GlobalSummary | null>(null);
  const [topInsights, setTopInsights] = useState<TopInsight[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    void fetch("/api/admin/ai/insights", { credentials: "include" })
      .then((r) => r.json())
      .then((d: { summary: GlobalSummary; top_insights: TopInsight[] }) => {
        setSummary(d.summary ?? null);
        setTopInsights(d.top_insights ?? []);
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="flex h-48 items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div>
        <h2 className="flex items-center gap-2 text-base font-semibold">
          <Lightbulb className="h-4 w-4 text-violet-500" />
          Oportunidades IA — Vista global
        </h2>
        <p className="text-xs text-muted-foreground">Últimos 7 días · Todos los tenants</p>
      </div>

      {summary && (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {[
            { icon: Users, label: "Tenants con insights", value: summary.total_tenants, color: "text-blue-600" },
            { icon: AlertTriangle, label: "Oportunidades críticas abiertas", value: summary.total_critical, color: "text-red-600" },
            { icon: Lightbulb, label: "Oportunidades abiertas", value: summary.total_open, color: "text-amber-600" },
            { icon: TrendingUp, label: "Acciones aplicadas", value: summary.total_applied, color: "text-emerald-600" },
          ].map((kpi) => (
            <div key={kpi.label} className="rounded-xl border bg-muted/20 p-4">
              <div className="flex items-center gap-2">
                <kpi.icon className={`h-4 w-4 ${kpi.color}`} />
                <span className="text-xs text-muted-foreground">{kpi.label}</span>
              </div>
              <div className="mt-2 text-2xl font-bold tabular-nums">{kpi.value}</div>
            </div>
          ))}
        </div>
      )}

      {topInsights.length > 0 && (
        <div>
          <h3 className="mb-3 text-sm font-medium">Oportunidades más frecuentes (7d)</h3>
          <div className="space-y-2">
            {topInsights.map((ins) => (
              <div key={ins.insight_id} className="flex items-center justify-between rounded-lg border bg-muted/10 px-4 py-2">
                <span className="text-sm font-mono text-muted-foreground">{ins.insight_id}</span>
                <span className="text-sm font-bold tabular-nums">{ins.frequency}×</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {!summary && (
        <div className="flex h-32 items-center justify-center text-muted-foreground text-sm">
          Sin datos de insights aún.
        </div>
      )}
    </div>
  );
}
